/**
 * Browser entry point.
 *
 * Composes nothing itself — it reads the URL, hands off to `boot`, and installs
 * the capture harness. Anything resembling game logic belongs in a subsystem.
 */

import { boot, type Game } from './boot.js'
import { installHarness } from './dev/harness.js'
import { skirmishSlots } from './sim/scenario.js'
import { Chat } from './ui/chat.js'
import { Lobby } from './ui/lobby.js'

/**
 * How long a lockstep stall must last before it is worth telling the player
 * about. Below this it is a hitch; above it, it looks like a crash.
 */
const STALL_GRACE_MS = 1200

/**
 * Skirmish AI levels by name, shared by `?ai=` and the menu's picker. Passive
 * is a testing target — the AI never acts at all — not a fourth difficulty.
 */
const AI_LEVELS: Record<string, number> = { easy: 0, normal: 1, hard: 2, passive: 3 }

declare global {
  interface Window {
    /** Capture-harness sentinel. Set once the first frame has rendered. */
    __READY__?: boolean
    __BA__?: Game
  }
}

function main(): void {
  const mount = document.getElementById('app')
  if (!mount) throw new Error('#app not found')

  const params = new URLSearchParams(location.search)
  const design = params.get('design')
  if (import.meta.env.DEV && design === 'terrain') {
    void import('./dev/terrain-lab.js').then(({ installTerrainLab }) => installTerrainLab(mount))
    return
  }
  if (import.meta.env.DEV && design === 'roster') {
    void import('./dev/roster-lab.js').then(({ installRosterLab }) => installRosterLab(mount))
    return
  }
  if (import.meta.env.DEV && (design === 'tanks' || design === 'infantry')) {
    void import('./dev/tank-lab.js').then(({ installTankLab }) => installTankLab(mount, design))
    return
  }
  const seed = params.get('seed') ?? 'blue-alert'
  const players = Number(params.get('players') ?? '2')

  // `?harness=1` boots nothing and waits for the capture tool to pick a shot,
  // so a capture never races a half-started match.
  if (params.get('harness') === '1') {
    installHarness(mount)
    window.__READY__ = true
    return
  }

  // `?instant=1` finishes every build the tick it is queued, for trying things
  // out without sitting through a build order. It is a match setting rather
  // than a client toggle because it changes simulation timing — see
  // `MatchDefinition.instantBuild`.
  const instantBuild = params.get('instant') === '1'
  const difficulty = AI_LEVELS[params.get('ai') ?? 'normal'] ?? AI_LEVELS.normal
  // `?speed=3` starts the match at three times wall-clock. Pacing only, so it
  // does not have to match the other player's setting — see `boot.ts`.
  const speed = Number(params.get('speed') ?? '1')

  const startSkirmish = (aiLevel?: string): void => {
    const game = boot({
      mount,
      match: {
        mapSeed: seed,
        seed,
        // `?ai=easy|normal|hard|passive`, or the menu's picker when the game
        // starts from there. Part of the match rather than a client toggle: it
        // changes what the AI does, so both sides have to agree.
        slots: skirmishSlots(
          Number.isFinite(players) ? players : 2,
          0,
          AI_LEVELS[aiLevel ?? ''] ?? difficulty,
        ),
        instantBuild,
      },
      localSlot: 0,
      speed: Number.isFinite(speed) ? speed : 1,
    })
    window.__BA__ = game
    window.__READY__ = true
    console.info(
      `[bluealert] booted seed=${seed}${instantBuild ? ' instant-build' : ''} ` +
        `subsystems=[${game.sim.registry.order.join(', ')}]`,
    )
  }

  // `?play=1` skips the menu and drops straight into a skirmish, which is what
  // every link written before the menu existed expects — and what you want when
  // you are reloading the same scenario for the twentieth time.
  if (params.get('play') === '1') {
    startSkirmish()
    return
  }

  // Everything else opens the menu: single player on one side, the list of open
  // rooms on the other. It used to boot a skirmish here, which made single
  // player the only mode reachable without knowing to add `?mp=1` to the URL.
  showMenu(mount, params, instantBuild, Number.isFinite(speed) ? speed : 1, startSkirmish)
}

/**
 * Hand the page to the lobby, and boot the match around the socket it opened.
 *
 * The relay is created by the lobby rather than by `Sim`, because it is already
 * connected and mid-conversation by the time the match exists: it is what told
 * us the seed, the slots, and which slot is ours.
 */
function showMenu(
  mount: HTMLElement,
  params: URLSearchParams,
  instantBuild: boolean,
  speed: number,
  onSinglePlayer: (aiLevel?: string) => void,
): void {
  const wsProtocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  const relayUrl = params.get('relay') ?? `${wsProtocol}//${location.host}/join`

  const lobby = new Lobby({
    root: mount,
    relayUrl,
    onSinglePlayer,
    onStart(relay, slots) {
      const game = boot({
        mount,
        match: {
          mapSeed: relay.mapSeed,
          seed: relay.seed,
          slots,
          instantBuild,
        },
        localSlot: relay.localSlot,
        speed,
        // The relay is handed the simulation's command world here, not at
        // construction: it has been alive since the lobby, talking to a
        // placeholder that owns nothing.
        relay: (world) => {
          relay.attach(world)
          return relay
        },
      })
      window.__BA__ = game
      window.__READY__ = true

      // Chat exists only in multiplayer, so it is built here rather than in
      // `boot`: there is nobody to talk to in a skirmish.
      const chat = new Chat({
        root: mount,
        localSlot: relay.localSlot,
        send: (text) => relay.sendChat(text),
      })
      chat.note('Press ENTER to chat.')

      // From here the lobby is hidden, so relay events have to surface in the
      // game instead. A stall in particular: the simulation is *correctly*
      // waiting for a peer, and without a word on screen that is
      // indistinguishable from a freeze.
      const net = game.netStatus
      net?.attach(relay)

      // A momentary stall is normal and not worth saying: it happens on every
      // hitch, and it happens constantly when the two players are on different
      // speed settings, since the faster one waits at the barrier by design.
      // The banner is for a stall that has lasted long enough to look broken.
      let stallTimer = 0
      lobby.onGameEvent = (event) => {
        switch (event.kind) {
          case 'stalled':
            net?.note(`stalled at tick ${event.tick}`)
            if (stallTimer === 0) {
              stallTimer = window.setTimeout(() => {
                net?.setBanner(`Waiting for the other player (tick ${event.tick})…`, true)
              }, STALL_GRACE_MS)
            }
            break
          case 'resumed':
            window.clearTimeout(stallTimer)
            stallTimer = 0
            net?.setBanner('')
            net?.note(`resumed at tick ${event.tick}`)
            break
          case 'late':
            net?.note(`player ${event.slot} is falling behind`)
            break
          case 'chat':
            chat.append(event.slot, event.text)
            break
          case 'dropped':
            net?.setBanner(`Player ${event.slot} was dropped (${event.reason}).`, true)
            net?.note(`player ${event.slot} dropped: ${event.reason}`)
            chat.note(`Player ${event.slot + 1} left the match.`)
            break
          case 'desync':
            net?.setBanner(`Desync between ticks ${event.from} and ${event.to}. Match over.`, true)
            net?.note(`desync ${event.from}..${event.to}`)
            break
          case 'error':
            net?.setBanner(
              event.reason === 'reconnect-failed'
                ? 'Could not get back into the match.'
                : `Relay error: ${event.reason}`,
              true,
            )
            net?.note(`error: ${event.reason}`)
            break
          case 'closed':
            net?.setBanner('Connection lost — reconnecting…', true)
            net?.note('socket closed')
            break
          case 'connected':
            // Back after a drop. The simulation never advanced while the
            // socket was down — a lockstep client cannot run a tick it has no
            // committed set for — so there is nothing to catch up, only a
            // banner to take down.
            net?.setBanner('')
            net?.note(`reconnected in slot ${event.slot}`)
            break
          default:
            break
        }
      }
    },
  })
  void lobby
  window.__READY__ = true
}

try {
  main()
} catch (err) {
  console.error('[bluealert] boot failed', err)
  const el = document.getElementById('app')
  if (el) {
    el.textContent = `boot failed: ${err instanceof Error ? err.message : String(err)}`
    el.setAttribute('style', 'padding:2rem;font:14px ui-monospace,monospace;color:#e88')
  }
}
